import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'
import { useTranslation } from 'react-i18next'
import { STATUS_COLORS } from './complianceStatus'

// Compact donut summarizing a society's compliance items by status, with the
// completion percentage centered inside the ring.
export default function ComplianceRing({ compliances, size = 120 }) {
  const { t } = useTranslation()

  const { data, percentage } = useMemo(() => {
    const total = compliances?.length || 0
    const counts = { complete: 0, pending: 0, expired: 0 }
    compliances?.forEach((c) => {
      counts[c.status] = (counts[c.status] || 0) + 1
    })
    const pct = total ? Math.round((counts.complete / total) * 100) : 0

    return {
      percentage: pct,
      data: [
        { key: 'complete', value: counts.complete },
        { key: 'pending', value: counts.pending },
        { key: 'expired', value: counts.expired }
      ].filter((d) => d.value > 0)
    }
  }, [compliances])

  const ringColor =
    percentage >= 80 ? STATUS_COLORS.complete : percentage >= 50 ? STATUS_COLORS.pending : STATUS_COLORS.expired

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <ResponsiveContainer width='100%' height='100%'>
        <PieChart>
          <Pie
            data={data.length ? data : [{ key: 'empty', value: 1 }]}
            dataKey='value'
            cx='50%'
            cy='50%'
            innerRadius='70%'
            outerRadius='100%'
            startAngle={90}
            endAngle={-270}
            stroke='none'
            isAnimationActive
          >
            {(data.length ? data : [{ key: 'empty' }]).map((entry) => (
              <Cell key={entry.key} fill={data.length ? STATUS_COLORS[entry.key] : '#e0e0e0'} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 800, color: ringColor, lineHeight: 1 }}>
          {percentage}%
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.6rem' }}>
          {t('compliant')}
        </Typography>
      </Box>
    </Box>
  )
}
